import { useCallback, useMemo, useState, MouseEvent, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import styled from '@emotion/styled';
import { IconButton } from '@kep-ui-kit/ui-components';
import { MoreHorizontal24Icon } from '@kep-ui-kit/ui-icons';
import { useTranslation } from 'react-i18next';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';
import { withErrorHandling } from '@selfserve/react-hocs';
import { StyledMenu, StyledMenuItem } from '@selfserve/react-ui';
import {
  ApplicationFeatures,
  buttonIds,
  PageType,
  subscriptionStatuses,
  trackPageLoadEvent
} from '@selfserve/react-utils';
import {
  AddOnItem,
  PlanItemInfo,
  usePlanUnsubscribe,
  usePlanUpdate,
} from '../data';
import { PlanOverviewPaymentInfo } from '../ui/plan-overview-payment-info';
import { PlanManagingModal } from '../ui/plan-managing-modal';
import { AddonsList } from '../ui/addons-list';
import { PlansList } from '../ui/plans-list';
import { IContextData } from './account-layout';
import { KmeLoginPage } from '../../../../react-feature-purchase-plan/src/lib/ui/KmeLoginPage';

const StyledPlansOverviewContainer = styled('div')`
  margin-bottom: 32px;
`;

export const StyledInfoContainer = styled('div')(
  ({ theme, isRow }: { theme?: any; isRow?: boolean }) => ({
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('lg')]: {
      ...(isRow && {
        flexDirection: 'row',
        '> div': {
          '&:not(&:last-of-type)': {
            marginBottom: 0,
            marginRight: theme.spacing(2),
          },
        },
      }),
    },
    '&:not(&:last-of-type)': {
      marginBottom: theme.spacing(4),
    },
  })
);

export const StyledItemContainer = styled('div')(
  ({ theme, smallRadius }: { theme?: any; smallRadius?: boolean }) => ({
    display: 'flex',
    width: '100%',
    border: `1px solid ${theme.palette.grey[400]}`,
    borderRadius: smallRadius ? '4px' : '8px',
    padding: theme.spacing(3),
    paddingRight: theme.spacing(4),
    '&:not(&:last-of-type)': {
      marginBottom: theme.spacing(2),
    },
    '> div:first-of-type': {
      marginRight: 'auto',
    },
    '> :last-of-type': {
      height: 'fit-content',
    },
    '&:hover': {
      borderColor: theme.palette.primary.main,
      '.overview-information-label': {
        color: theme.palette.primary.main,
      },
    },
  })
);

export const StyledStatusContainer = styled('div')(
  ({ theme, status }: { theme?: any; status: string }) => ({
    cursor: 'context-menu',
    position: 'relative',
    backgroundColor: theme.palette.grey['600'],
    fontSize: '13px',
    fontFamily: 'Centra No1',
    fontWeight: 600,
    padding: '3px 4px',
    marginRight: theme.spacing(3),
    marginLeft: '2px',
    borderRadius: '4px',
    '&:hover': {
      p: {
        display: 'block',
      },
    },
    span: {
      color: theme.palette.grey['200'],
      ...(status === subscriptionStatuses.CANCELLED && {
        color: theme.palette.danger.main,
      }),
    },
    p: {
      display: 'none',
      position: 'absolute',
      width: '208px',
      height: '52px',
      borderRadius: '4px',
      padding: '8px',
      margin: 0,
      background: theme.palette.grey[100],
      fontWeight: 600,
      fontStyle: 'normal',
      fontSize: '14px',
      lineHeight: '18px',
      color: '#FFF',
      top: '-64px',
      left: '50%',
      marginLeft: '-104px',
      '&::after': {
        content: '""',
        position: 'absolute',
        top: '52px',
        left: '50%',
        marginLeft: '-7px',
        pointerEvents: 'none',
        width: 0,
        height: 0,
        borderLeft: '7px solid transparent',
        borderRight: '7px solid transparent',
        borderTop: `9px solid ${theme.palette.grey[100]}`,
      },
    },
  })
);

const StyledMenuButton = styled(IconButton)(
  ({ theme, open }: { theme?: any; open?: boolean }) => ({
    ...(open && { backgroundColor: theme.palette.grey['600'] }),
    '&:hover': {
      backgroundColor: theme.palette.grey['600'],
    },
  })
);

const validationSchema = (isUnsubscribe: boolean) =>
  Yup.object().shape({
    ...(!isUnsubscribe && {
      extendedInfo: Yup.string().required(),
    }),
  });

export const MenuButton = ({
  open,
  onClick,
  ...rest
}: {
  open: boolean;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  id?: string;
}) => (
  <a className="more" {...rest}>
    <StyledMenuButton
      aria-controls="basic-menu"
      aria-haspopup="true"
      aria-expanded={open}
      onClick={onClick}
      open={open}
    >
      <MoreHorizontal24Icon />
    </StyledMenuButton>
  </a>
);

interface LoginByPostData {
  url?: string;
  triggered?: boolean;
  type?: string;
  body?: {
    ref?: string;
    userId?: string;
    userKs?: string;
  };
}

export interface FormValues {
  reason: string;
  extendedInfo: string;
}

export const PlansOverview = withErrorHandling(({ openErrorModal }) => {
  const { t } = useTranslation();
  const { subscriptionOverviewData } = useOutletContext<IContextData>() || {};
  const { subscriptionInfoList, nextInvoiceDate, recurringCharges } =
    subscriptionOverviewData || {};
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isAddOn, setIsAddOn] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUnsubscribe, setIsUnsubscribe] = useState(false);
  const [validateOnChange, setValidateOnChange] = useState(false);
  const [id, setId] = useState<string>('');
  const [loginByPost, setLoginByPost] = useState<LoginByPostData>();
  const {
    mutateAsync: handleUnsubscribeRequest,
    data: unsubscribeData,
    isLoading: unsubscribeLoading,
  } = usePlanUnsubscribe(id);
  const {
    mutateAsync: handleUpdateRequest,
    data: updateData,
    isLoading: updateLoading,
  } = usePlanUpdate(id);
  const hasActiveSubscription = useMemo(
    () =>
      !!subscriptionInfoList?.find(
        ({ status }) => status === subscriptionStatuses.ACTIVE
      ),
    [subscriptionInfoList]
  );
  const plans = useMemo(
    () =>
      subscriptionInfoList?.filter((subscription) =>
        subscription.plan?.hasOwnProperty('visible')
          ? subscription.plan.visible
          : true
      ),
    [subscriptionInfoList]
  );
  const addons = useMemo(
    () =>
      subscriptionInfoList
        ?.map((plan) =>
          plan.addons?.map((addon) => ({
            ...addon,
            status: plan.status,
            id: plan.id,
          }))
        )
        .flat(),
    [subscriptionInfoList]
  );
  const addonPresentationInfo = useMemo(
    () =>
      subscriptionInfoList?.map((i) => i.addOnPresentationInfo).flat() || [],
    [subscriptionInfoList]
  );
  const planOptions = useMemo(
    () =>
      plans?.map((planItem) => ({
        text: planItem?.plan?.displayName as string,
        value: planItem?.id as string,
      })) || [],
    [plans]
  );
  const addonOptions = useMemo(
    () =>
      addons?.map((addon) => ({
        text: addon?.displayName as string,
        value: addon?.name as string,
      })) || [],
    [addons]
  );
  const selectedItem = useMemo(
    () =>
      isAddOn
        ? addons?.find((addon) => addon?.name === id)
        : plans?.find((planItem) => planItem?.id === id),
    [isAddOn, addons, plans, id]
  );
  const open = useMemo(() => Boolean(anchorEl), [anchorEl]);
  const formik: FormikProps<FormValues> = useFormik<FormValues>({
    validateOnChange,
    validateOnBlur: true,
    validationSchema: validationSchema(isUnsubscribe),
    initialValues: { reason: '', extendedInfo: '' },
    onSubmit: (values) => {
      if (isUnsubscribe) {
        handleUnsubscribeRequest(values);
      } else {
        handleUpdateRequest(values);
      }
      handleMenuClose();
      handleModalToggle()();
    },
  });

  const handleMenuClick = useCallback(
    (isAddOn: boolean, id: string) => (event: MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
      setIsAddOn(isAddOn);
      setId(id);
    },
    []
  );

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleModalToggle = useCallback(
    (isUnsubscribe?: boolean) => () => {
      setIsModalOpen((value) => {
        if (value) {
          setId('');
          formik.setFieldValue('reason', '');
          formik.setFieldValue('extendedInfo', '');
        }
        return !value;
      });
      if (open) handleMenuClose();
      if (typeof isUnsubscribe === 'boolean') setIsUnsubscribe(isUnsubscribe);
    },
    [open, handleMenuClose, setIsUnsubscribe, setId]
  );

  const handleGoToApp = useCallback(
    (id: string) => () => {
      const currentPlan = plans?.find((p) => p.id === id);
      if (currentPlan?.loginUrl?.type !== 'POST') {
        window.open(currentPlan?.loginUrl?.url, '_blank');
      } else {
        setLoginByPost({
          triggered: true,
          ...currentPlan?.loginUrl,
        });
      }
      if (open) handleMenuClose();
    },
    [id, plans, open, handleMenuClose, setLoginByPost]
  );

  useEffect(() => {
    const hasError = !!Object.keys(formik.errors).length;
    if (hasError && !validateOnChange) {
      setValidateOnChange(true);
    } else if (!hasError) {
      setValidateOnChange(false);
    }
  }, [validateOnChange, formik.errors]);

  useEffect(() => {
    if (unsubscribeData?.status && unsubscribeData?.status !== 201) {
      openErrorModal();
    }
    if (updateData?.status && updateData?.status !== 201) {
      openErrorModal();
    }
  }, [
    unsubscribeData,
    unsubscribeLoading,
    updateData,
    updateLoading,
    openErrorModal,
  ]);

  useEffect(() => {
    trackPageLoadEvent(
      PageType.View,
      'account_management_plans_overview_page',
      ApplicationFeatures.ACCOUNT_MANAGEMENT,
    );
  }, []);

  return (
    <StyledPlansOverviewContainer>
      {nextInvoiceDate && recurringCharges && hasActiveSubscription && (
        <PlanOverviewPaymentInfo
          nextInvoiceDate={nextInvoiceDate}
          recurringCharges={recurringCharges}
        />
      )}
      <PlansList
        plans={plans as Array<PlanItemInfo>}
        open={open}
        selectedId={id}
        handleMenuClick={handleMenuClick}
        handleGoToApp={handleGoToApp}
      />
      <KmeLoginPage
        triggered={!!loginByPost?.triggered}
        url={loginByPost?.url}
        requestType={loginByPost?.type}
        body={loginByPost?.body}
      />
      <AddonsList
        addons={addons as Array<AddOnItem>}
        addonPresentationInfo={addonPresentationInfo as Array<string>}
        open={open}
        selectedId={id}
        handleMenuClick={handleMenuClick}
      />
      <StyledMenu
        anchorEl={anchorEl}
        open={!!open}
        onClose={handleMenuClose}
        variant="selectedMenu"
        id={buttonIds.planMenu}
        width={260}
      >
        {!isAddOn && (
          <StyledMenuItem
            onClick={handleGoToApp(id)}
            id={buttonIds.planGoToApp}
          >
            <span className="p-menuitem-text">{t('common.go-to-app')}</span>
          </StyledMenuItem>
        )}
        <StyledMenuItem
          id={isAddOn ? buttonIds.addonPlanUpdate : buttonIds.planUpdatePlan}
          onClick={handleModalToggle(false)}
        >
          <span className="p-menuitem-text">
            {isAddOn ? t('common.update-addon') : t('common.update-plan')}
          </span>
        </StyledMenuItem>
        <StyledMenuItem
          onClick={handleModalToggle(true)}
          id={isAddOn ? buttonIds.addonUnsubscribe : buttonIds.planUnsubscribe}
        >
          <span>{t('common.unsubscribe')}</span>
        </StyledMenuItem>
      </StyledMenu>
      <PlanManagingModal
        open={isModalOpen}
        handleClose={handleModalToggle()}
        isUnsubscribe={isUnsubscribe}
        isAddOn={isAddOn}
        planOptions={planOptions}
        addonOptions={addonOptions}
        selectedItem={selectedItem}
        formik={formik}
        setId={setId}
      />
    </StyledPlansOverviewContainer>
  );
});
